import type {
  NodeComponentProps,
  NodeViewLiveProps,
  NodeViewStaticProps,
} from '../tiptap-helpers'
import type { FileNodeAttributes } from './FileNode'

import { NodeViewWrapper } from '@tiptap/react'

import {
  faFile,
  faFileAudio,
  faFileCsv,
  faFileExcel,
  faFilePdf,
  faFilePowerpoint,
  faFileVideo,
  faFileWord,
  faFileZipper,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  CSV,
  DOC,
  DOCX,
  GZIP,
  ODP,
  ODS,
  ODT,
  PDF,
  PPT,
  PPTX,
  RAR,
  TAR,
  XLS,
  XLSX,
  ZIP,
  ZIP7,
} from '@/constants'

export function FileNodeViewLive({ node }: NodeViewLiveProps) {
  return (
    <NodeViewWrapper>
      <FileNodeComponent node={node} />
    </NodeViewWrapper>
  )
}

export function FileNodeViewStatic({ node }: NodeViewStaticProps) {
  return <FileNodeComponent node={node} />
}

/**
 * Custom Tiptap node that renders uploaded files.
 *
 * This component displays a file icon and information, and allows users to
 * download the file by clicking a button.
 *
 * @note Not exported, use the static or live wrappers depending on usecase.
 */
function FileNodeComponent({ node }: NodeComponentProps) {
  const attrs = node.attrs as FileNodeAttributes // TODO: validate instead of assert

  return (
    <a
      href={attrs.url}
      target="_blank"
      rel="noreferrer"
      className="my-2 w-full no-underline! bg-white/10 hover:bg-white/20 p-1 px-2 rounded-sm shadow-sm flex items-center gap-3"
      title="Click to download"
      draggable={false} // Prevent native dragging (not draghandle)
      tabIndex={-1}
    >
      <FontAwesomeIcon
        icon={getFileTypeIcon(attrs.type)}
        className="flex-none text-3xl text-white"
      />
      <div className="grow text-left overflow-hidden">
        <div className="text-white font-semibold truncate">
          {attrs.filename}
        </div>
        <div className="text-white/60 text-xs truncate">{attrs.size}</div>
      </div>
    </a>
  )
}

function getFileTypeIcon(filetype: string) {
  switch (filetype) {
    case PDF:
      return faFilePdf
    case CSV:
      return faFileCsv
    case DOC || DOCX || ODT:
      return faFileWord
    case XLS || XLSX || ODS:
      return faFileExcel
    case PPT || PPTX || ODP:
      return faFilePowerpoint
    case ZIP || ZIP7 || RAR || GZIP || TAR:
      return faFileZipper
  }
  if (filetype.startsWith('audio')) return faFileAudio
  if (filetype.startsWith('video')) return faFileVideo
  return faFile
}
